/*
 * SPDX-License-Identifier: Apache-2.0
 *
 * The OpenSearch Contributors require contributions made to
 * this file be licensed under the Apache-2.0 license or a
 * compatible open source license.
 *
 * Modifications Copyright OpenSearch Contributors. See
 * GitHub history for details.
 */

/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import React from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
var sizeToClassNameMap = {
  full: 'ouiHorizontalRule--full',
  half: 'ouiHorizontalRule--half',
  quarter: 'ouiHorizontalRule--quarter'
};
export var SIZES = Object.keys(sizeToClassNameMap);
var marginToClassNameMap = {
  none: null,
  xs: 'ouiHorizontalRule--marginXSmall',
  s: 'ouiHorizontalRule--marginSmall',
  m: 'ouiHorizontalRule--marginMedium',
  l: 'ouiHorizontalRule--marginLarge',
  xl: 'ouiHorizontalRule--marginXLarge',
  xxl: 'ouiHorizontalRule--marginXXLarge'
};
export var MARGINS = Object.keys(marginToClassNameMap);
export var OuiHorizontalRule = ({
  className,
  size = 'full',
  margin = 'l',
  ...rest
}) => {
  const classes = classNames('ouiHorizontalRule', sizeToClassNameMap[size], marginToClassNameMap[margin], className);
  return <hr className={classes} {...rest} />;
};
OuiHorizontalRule.propTypes = {
  /**
     * Defines the width of the HR.
     */
  size: PropTypes.oneOf(["full", "half", "quarter"]),
  margin: PropTypes.oneOf(["none", "xs", "s", "m", "l", "xl", "xxl"]),
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string
};
try {
  OuiHorizontalRule.__docgenInfo = {
    tags: {},
    filePath: '/codebuild/output/src2210237963/src/src/components/horizontal_rule/horizontal_rule.tsx',
    description: '',
    displayName: 'OuiHorizontalRule',
    methods: [],
    props: {
      size: {
        defaultValue: {
          value: 'full'
        },
        description: 'Defines the width of the HR.',
        name: 'size',
        parent: {
          fileName: 'src/src/components/horizontal_rule/horizontal_rule.tsx',
          name: 'OuiHorizontalRuleProps'
        },
        declarations: [{
          fileName: 'src/src/components/horizontal_rule/horizontal_rule.tsx',
          name: 'OuiHorizontalRuleProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"full" | "half" | "quarter"',
          value: [{
            value: '"full"'
          }, {
            value: '"half"'
          }, {
            value: '"quarter"'
          }]
        }
      },
      margin: {
        defaultValue: {
          value: 'l'
        },
        description: '',
        name: 'margin',
        parent: {
          fileName: 'src/src/components/horizontal_rule/horizontal_rule.tsx',
          name: 'OuiHorizontalRuleProps'
        },
        declarations: [{
          fileName: 'src/src/components/horizontal_rule/horizontal_rule.tsx',
          name: 'OuiHorizontalRuleProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"xs" | "s" | "m" | "l" | "xl" | "xxl" | "none"',
          value: [{
            value: '"xs"'
          }, {
            value: '"s"'
          }, {
            value: '"m"'
          }, {
            value: '"l"'
          }, {
            value: '"xl"'
          }, {
            value: '"xxl"'
          }, {
            value: '"none"'
          }]
        }
      },
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: '',
        name: 'aria-label',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      }
    },
    extendedInterfaces: ['OuiHorizontalRuleProps', 'CommonProps', 'HTMLAttributes', 'AriaAttributes', 'DOMAttributes']
  };
} catch (e) {}